import { useQuery } from "@tanstack/react-query";
import { MessageSquareText } from "lucide-react";
import { useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Pagination } from "../components/pagination";
import type { AdminMessage, CursorQuery } from "../lib/api";
import { adminApi } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { formatDateTime, formatTokens } from "../lib/format";

const PAGE_SIZE = 50;

const DELIVERY_LABELS: Record<AdminMessage["deliveryStatus"], string> = {
  NOT_APPLICABLE: "Входящее",
  PENDING: "Ожидает отправки",
  ACCEPTED: "Принято Wazzup",
  SENT: "Отправлено",
  DELIVERED: "Доставлено",
  READ: "Прочитано",
  UNKNOWN: "Доставка не подтверждена",
  FAILED: "Не отправлено",
};

function MessageRow({ message }: { message: AdminMessage }) {
  const roleLabel = message.role === "USER" ? "Клиент" : "AI-агент";
  const hasMetadata =
    message.model !== null ||
    message.tokens !== null ||
    message.latencyMs !== null;

  return (
    <article
      className={`panel w-full max-w-[85%] overflow-hidden ${
        message.role === "USER" ? "ml-auto" : "mr-auto bg-accent/30"
      }`}
      data-testid="message-row"
      data-message-id={message.id}
      data-role-label={roleLabel}
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{roleLabel}</span>
          <span
            className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground"
            data-testid={`delivery-status-${message.id}`}
          >
            {DELIVERY_LABELS[message.deliveryStatus]}
          </span>
        </div>
        <time
          className="text-xs text-muted-foreground"
          dateTime={message.createdAt}
        >
          {formatDateTime(message.createdAt)}
        </time>
      </header>
      <p
        className="whitespace-pre-wrap break-words p-4 text-sm"
        data-testid={`message-content-${message.id}`}
      >
        {message.content}
      </p>
      {hasMetadata ? (
        <footer className="flex flex-wrap gap-x-4 gap-y-1 border-t px-4 py-3 text-xs text-muted-foreground">
          {message.model !== null ? <span>Модель: {message.model}</span> : null}
          {message.tokens !== null ? (
            <span>Токены: {formatTokens(message.tokens)}</span>
          ) : null}
          {message.latencyMs !== null ? (
            <span>Задержка: {message.latencyMs} мс</span>
          ) : null}
        </footer>
      ) : null}
    </article>
  );
}

function DialogMessages({
  accountId,
  dialogId,
  funnelId,
}: {
  accountId: string;
  dialogId: string;
  funnelId?: string;
}) {
  const { token } = useAdminAuth();
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const currentCursor = cursorStack[pageIndex] ?? null;
  const query = useMemo<CursorQuery & { funnelId?: string }>(
    () => ({
      ...(funnelId ? { funnelId } : {}),
      ...(currentCursor ? { cursor: currentCursor } : {}),
      take: PAGE_SIZE,
    }),
    [currentCursor, funnelId],
  );

  const messages = useQuery({
    queryKey: [
      "admin",
      "account",
      accountId,
      ...(funnelId ? ["funnel", funnelId] : []),
      "dialog",
      dialogId,
      "messages",
      query,
    ],
    queryFn: () => adminApi.dialogMessages(token!, accountId, dialogId, query),
    enabled: Boolean(token),
    staleTime: 0,
    refetchOnMount: "always",
    gcTime: 0,
  });

  function handleForward() {
    const nextCursor = messages.data?.nextCursor;
    if (!nextCursor) return;
    setCursorStack((current) => [
      ...current.slice(0, pageIndex + 1),
      nextCursor,
    ]);
    setPageIndex((current) => current + 1);
  }

  return (
    <div>
      <PageHeader
        title={`Диалог ${dialogId}`}
        description="Хронологическая история сообщений только для чтения."
        backTo={
          funnelId
            ? `/funnels/${funnelId}/dialogs`
            : `/accounts/${accountId}/agents`
        }
        backLabel={funnelId ? "К диалогам агента" : "К агентам клиента"}
      />

      {messages.isLoading ? (
        <div className="panel p-4 text-sm text-muted-foreground">
          Загружаем сообщения...
        </div>
      ) : messages.error ? (
        <div className="panel p-4 text-sm text-danger">
          {messages.error.message}
        </div>
      ) : messages.data?.items.length === 0 ? (
        <div className="panel flex items-center gap-3 p-4 text-sm text-muted-foreground">
          <MessageSquareText className="h-5 w-5" />В диалоге пока нет сообщений.
        </div>
      ) : (
        <div className="space-y-3">
          {(messages.data?.items ?? []).map((message) => (
            <MessageRow key={message.id} message={message} />
          ))}
        </div>
      )}

      <Pagination
        canGoBack={pageIndex > 0}
        canGoForward={Boolean(messages.data?.nextCursor)}
        onBack={() => setPageIndex((current) => Math.max(0, current - 1))}
        onForward={handleForward}
        isLoading={messages.isFetching}
      />
    </div>
  );
}

export function DialogPage() {
  const { accountId, funnelId, dialogId } = useParams();
  const { token } = useAdminAuth();
  const funnel = useQuery({
    queryKey: ["admin", "funnel", funnelId],
    queryFn: () => adminApi.funnel(token!, funnelId!),
    enabled: Boolean(token && funnelId),
  });

  if (!dialogId || (!accountId && !funnelId)) {
    return <Navigate to="/accounts" replace />;
  }

  if (funnelId) {
    if (funnel.isLoading) {
      return (
        <div className="panel p-4 text-sm text-muted-foreground">
          Загружаем агента...
        </div>
      );
    }
    if (funnel.error || !funnel.data) {
      return (
        <div className="panel p-4 text-sm text-danger">
          {funnel.error?.message ?? "Агент не найден"}
        </div>
      );
    }

    return (
      <DialogMessages
        key={`${funnel.data.accountId}:${funnelId}:${dialogId}`}
        accountId={funnel.data.accountId}
        funnelId={funnelId}
        dialogId={dialogId}
      />
    );
  }

  return (
    <DialogMessages
      key={`${accountId}:${dialogId}`}
      accountId={accountId!}
      dialogId={dialogId}
    />
  );
}
